/**
 * Data Module atoms — derived row models for the Data table.
 *
 * Each atom flattens its respective catalog of layers into a normalized DataModuleRow[]
 * suitable for direct consumption by RiskDataTable.
 *
 * Dependency chain:
 *   layers.derived atoms (raw catalog)
 *     → flatten utils (pure transformation)
 *       → these atoms (normalized rows)
 */

import {
  contextualCatalogAtom,
  elementCatalogAtom,
  hazardCatalogAtom,
} from '@app/domains/layers/state/layers.derived'
import {
  flattenContextualFromCatalog,
  flattenHazardsFromCatalog,
  getBaseLayerId,
} from '@app/domains/layers/utils/layers.utils'
import { atom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import { DataModuleRow } from '../types'
import { layerMetadataAtom } from '@app/domains/layers/state/layers.atoms'

export const elementTableRowsAtom = atom<Promise<DataModuleRow[]>>(async (get) => {
  const catalog = await get(elementCatalogAtom)
  const rows: DataModuleRow[] = []

  for (const [categoryKey, category] of Object.entries(catalog)) {
    for (const group of category.groups ?? []) {
      for (const element of group.assets ?? []) {
        rows.push({
          id: `${categoryKey}::${group.groupName}::${element.source}`,
          type: element.type,
          label: 'Element',
          name: element.display_name,
          group: group.groupName === 'Ungrouped' ? '-' : group.groupName,
          owner: element.provider ?? null,
          lastUpdated: element.date_updated ?? null,
          unit: element.unit ?? null,
          category: category.title ?? categoryKey,
          icon: element.icon,
        })
      }
    }
  }

  return rows
})

export const contextualTableRowsAtom = atom<Promise<DataModuleRow[]>>(async (get) => {
  const catalog = await get(contextualCatalogAtom)
  const rows: DataModuleRow[] = []

  for (const { categoryKey, category, group, layer } of flattenContextualFromCatalog(catalog)) {
    const groupName = group.groupName ?? null

    rows.push({
      id: layer.id,
      type: layer.id,
      label: 'Contextual',
      name: layer.display_name,
      group: groupName === 'Ungrouped' ? '-' : groupName,
      owner: null,
      lastUpdated: null,
      unit: null,
      category: category.title ?? categoryKey,
      icon: layer.icon ?? null,
    })
  }

  return rows
})

export const hazardTableRowsAtom = atom<Promise<DataModuleRow[]>>(async (get) => {
  const hazardDetails = await get(hazardCatalogAtom)
  const layerMetadata = await get(layerMetadataAtom)
  const rows: DataModuleRow[] = []

  for (const { hazard, scenario } of flattenHazardsFromCatalog(hazardDetails)) {
    const hazardName = hazard.hazard_name ?? hazard.display_name ?? hazard.assetTag ?? 'Risk source'
    const scenarioName =
      scenario.display_name ??
      scenario.name ??
      scenario.scenario_name ??
      scenario.assetTag ??
      hazardName

    const meta =
      layerMetadata.find((m) => m.id === getBaseLayerId(scenario.assetTag))?.metadata ?? null
    const suitability =
      layerMetadata.find((m) => m.id === getBaseLayerId(scenario.assetTag))?.suitability ?? null

    rows.push({
      id: getBaseLayerId(scenario.assetTag),
      label: 'Hazard',
      assetTag: typeof hazard.assetTag === 'string' ? hazard.assetTag : undefined,
      type: scenario.assetTag,
      name: scenarioName,
      group: hazard.group ?? 'Hazards',
      owner: meta?.owner_name ?? null,
      confidenceRating: meta?.confidence_rating ?? null,
      suitability: suitability,
      lastUpdated: null,
      unit: null,
      category: scenario.hazard_title ?? hazardName,
      hazardName: meta?.title ?? scenario.hazard_name,
      icon: hazard.icon,
    })
  }

  return rows
})

export const allTableRowsAtom = atom<Promise<DataModuleRow[]>>(async (get) => {
  const [elements, hazards, contextual] = await Promise.all([
    get(elementTableRowsAtom),
    get(hazardTableRowsAtom),
    get(contextualTableRowsAtom),
  ])

  return [...elements, ...hazards, ...contextual]
})

export const hazardByIdAtom = atomFamily((assetTag: string) =>
  atom(async (get) => {
    const hazards = await get(hazardCatalogAtom)

    for (const hazard of hazards) {
      const scenario = hazard.scenarios?.find((s) => s.assetTag === assetTag)
      if (scenario) return { hazard, scenario }
    }

    return null
  }),
)

export const elementByIdAtom = atomFamily((assetTag: string) =>
  atom(async (get) => {
    const catalog = await get(elementCatalogAtom)
    let fallback

    for (const [categoryKey, category] of Object.entries(catalog)) {
      for (const group of category.groups ?? []) {
        const byType = group.assets?.find((e) => e.type === assetTag)
        if (byType) return { categoryKey, category, group, element: byType }

        if (!fallback) {
          const bySource = group.assets?.find((e) => e.source === assetTag)
          if (bySource) fallback = { categoryKey, category, group, element: bySource }
        }
      }
    }

    return fallback
  }),
)

export const contextualByIdAtom = atomFamily((layerId: string) =>
  atom(async (get) => {
    const catalog = await get(contextualCatalogAtom)

    for (const [categoryKey, category] of Object.entries(catalog)) {
      for (const group of category.groups ?? []) {
        const layer = group.layers?.find((l) => l.id === layerId)
        if (layer) return { categoryKey, category, group, layer }
      }
    }

    for (const [categoryKey, category] of Object.entries(catalog)) {
      for (const group of category.groups ?? []) {
        const layer = group.layers?.find((l) => l.display_name === layerId)
        if (layer) return { categoryKey, category, group, layer }
      }
    }

    return null
  }),
)

allTableRowsAtom.debugLabel = 'allTableRowsAtom'
elementTableRowsAtom.debugLabel = 'elementTableRowsAtom'
contextualTableRowsAtom.debugLabel = 'contextualTableRowsAtom'
hazardTableRowsAtom.debugLabel = 'hazardsTableRowsAtom'
